/**
 * Tool UIs for the long-running video jobs:
 *   - vc_render_status  (declarative project render)
 *   - vc_get_preview    (single-scene preview render)
 *   - vfx_get_job       (GPU pipeline stage job)
 *
 * Each is a single tool call that long-polls the service (via the tool's `wait`
 * param). While it runs, the card shows a LIVE progress bar + phase + ETA driven
 * by the `tool-progress` SSE channel (keyed by toolCallId). On completion it
 * collapses to a result summary (✓/✗ + key facts) with the raw JSON on demand.
 */
import { useEffect, useRef, useState } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { useToolProgress, type ToolProgressEvent } from '../useShellOutputStream';
import { Film, Clapperboard, Cpu, XCircle, AlertTriangle } from 'lucide-react';
import { GeneratedFiles } from './GeneratedFiles';
import { ToolRow, DetailPanel, DetailPre, plainText, wasInterrupted, INTERRUPTED_META } from '../toolRow';

type JobKind = 'vc-render' | 'vc-preview' | 'vfx-job';

interface JobUIConfig {
  toolName: string;
  kind: JobKind;
  title: string;
  Icon: typeof Film;
}

// ── Live ETA countdown ───────────────────────────────────────────────────────
// Progress events arrive every few seconds; tick the ETA down locally between
// them so the countdown feels live, re-syncing whenever a fresh event lands.
function useEtaCountdown(etaSec: number | null | undefined, running: boolean): number | null {
  const [eta, setEta] = useState<number | null>(etaSec ?? null);
  const endRef = useRef<number | null>(null);

  useEffect(() => {
    if (etaSec == null) { endRef.current = null; setEta(null); return; }
    endRef.current = Date.now() + etaSec * 1000;
    setEta(etaSec);
  }, [etaSec]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      if (endRef.current == null) return;
      const left = Math.max(0, Math.round((endRef.current - Date.now()) / 1000));
      setEta(left);
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  return eta;
}

function fmtEta(sec: number | null): string | null {
  if (sec == null) return null;
  if (sec <= 0) return 'finishing…';
  if (sec < 60) return `~${sec}s left`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `~${m}m${s ? ` ${s}s` : ''} left`;
}

/** Just the duration, so the unit can be set apart typographically. */
function fmtEtaValue(sec: number | null): string | null {
  if (sec == null || sec <= 0) return null;
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

// ── Phase rail ───────────────────────────────────────────────────────────────
// The stage sequence each job kind moves through. Rendered as dots rather than
// proportional segments on purpose: the phases are nowhere near equal in
// length (`capturing` alone is ~70% of a render), so drawing them to equal
// widths would be a progress bar that lies. The dots say WHICH phase; the bar
// beside them carries the only real magnitude we have.
const PHASE_RAILS: Record<JobKind, readonly string[]> = {
  'vc-render': ['queued', 'downloading', 'compiling', 'extracting', 'capturing', 'encoding', 'uploading'],
  'vc-preview': ['queued', 'downloading', 'compiling', 'extracting', 'capturing', 'encoding', 'uploading'],
  'vfx-job': ['queued', 'warming', 'running'],
};

/** Index of the live phase in its rail, or -1 when the phase is not a known step. */
function phaseIndex(kind: JobKind, live: ToolProgressEvent | null): number {
  const rail = PHASE_RAILS[kind];
  const candidates = [live?.phase, live?.status].filter(Boolean) as string[];
  for (const c of candidates) {
    const i = rail.indexOf(c.toLowerCase().trim());
    if (i !== -1) return i;
  }
  return -1;
}

/**
 * The server packs frame counts into `label` as "capturing 120/190". Pulling
 * them back out lets the count be set as its own figure instead of riding
 * along inside a string that gets truncated at the first tight width.
 */
function parseFrames(label?: string): { done: string; total: string } | null {
  const m = label?.match(/(\d+)\s*\/\s*(\d+)\s*$/);
  return m ? { done: m[1], total: m[2] } : null;
}

// ── Result summary helpers (per kind) ────────────────────────────────────────
function resultIsFailure(kind: JobKind, result: any): boolean {
  if (!result) return false;
  if (result.ok === false) return true;
  if (kind === 'vfx-job') return result.status === 'failed' || result.status === 'timed_out' || result.status === 'canceled';
  if (kind === 'vc-preview') return result.status === 'failed' || result.status === 'timed_out' || result.status === 'canceled';
  return result.status === 'failed' || result.status === 'timed_out' || result.status === 'canceled';
}

function resultIsSuccess(kind: JobKind, result: any): boolean {
  if (!result) return false;
  if (kind === 'vfx-job') return result.status === 'succeeded';
  if (kind === 'vc-preview') return result.status === 'ready';
  return result.status === 'completed';
}

function summaryLine(kind: JobKind, result: any): string | null {
  if (!result) return null;
  if (kind === 'vc-render' && result.result) {
    const r = result.result;
    const bits = [r.resolution, r.frames != null ? `${r.frames}f` : null, r.durationSeconds != null ? `${r.durationSeconds}s` : null]
      .filter(Boolean);
    return bits.join(' · ') || null;
  }
  if (kind === 'vc-preview' && result.status === 'ready') {
    return result.workspacePath ?? result.outputPath ?? null;
  }
  if (kind === 'vfx-job' && result.status === 'succeeded') {
    const n = result.outputs?.uris?.length ?? 0;
    return n > 0 ? `${n} artifact${n === 1 ? '' : 's'}` : 'done';
  }
  return null;
}

function errorLine(kind: JobKind, result: any, live: ToolProgressEvent | null): string | null {
  if (live?.error) return live.error;
  if (!result) return null;
  if (kind === 'vfx-job') return result.error?.message ?? result.error ?? null;
  return typeof result.error === 'string' ? result.error : null;
}

function savedPath(kind: JobKind, result: any): string | null {
  if (!result) return null;
  if (kind === 'vc-preview') return result.workspacePath ?? null;
  return result.savedPath ?? null;
}

// ── Card ──────────────────────────────────────────────────────────────────────
function JobCard({ cfg, ...props }: { cfg: JobUIConfig } & Record<string, any>) {
  const { toolCallId, args, result, status } = props;
  const isRunning = status?.type === 'running';
  const live = useToolProgress(isRunning ? toolCallId : undefined);

  const [expanded, setExpanded] = useState(false);

  const failed = !isRunning && resultIsFailure(cfg.kind, result);
  const succeeded = !isRunning && resultIsSuccess(cfg.kind, result);
  // Terminal but neither clearly success nor fail (e.g. `never_previewed`,
  // or `wait` elapsed mid-render) — show neutral.
  const neutralDone = !isRunning && !failed && !succeeded;

  const percent = live?.percent ?? null;
  // An estimated percent is still a percent: show the number and mark it as an
  // estimate below, rather than throwing it away for an indeterminate sweep.
  const indeterminate = isRunning && (percent == null || live?.phase === 'warming');
  const eta = useEtaCountdown(live?.etaSec, isRunning);
  const etaText = fmtEta(eta);
  const etaValue = fmtEtaValue(eta);

  const phaseText = live?.label || live?.phase || live?.status || (isRunning ? 'starting…' : null);
  const phaseAt = phaseIndex(cfg.kind, live);
  const frames = parseFrames(live?.label);
  // Frames get their own figure, so drop the "120/190" tail from the phase text.
  const phaseLabel = frames
    ? (phaseText?.replace(/\s*\d+\s*\/\s*\d+\s*$/, '').trim() || null)
    : phaseText;

  const { Icon } = cfg;
  const err = errorLine(cfg.kind, result, live);
  const summary = summaryLine(cfg.kind, result);
  const saved = savedPath(cfg.kind, result);

  return (
    <div className="my-0.5">
      <ToolRow
        icon={failed ? <XCircle className="text-red-500" /> : <Icon />}
        label={plainText(args?._thought) || cfg.title}
        running={isRunning}
        tone={failed ? 'error' : 'default'}
        meta={
          isRunning ? (live?.stalled ? <span className="inline-flex items-center gap-1 text-yellow-600"><AlertTriangle className="w-3 h-3" />stalled</span> : undefined)
            : succeeded ? summary ?? undefined
            : failed ? <span className="text-red-600">{result?.status ?? 'failed'}</span>
            : wasInterrupted(status, result) ? INTERRUPTED_META
            : neutralDone ? result?.status
            : undefined
        }
        open={expanded && !isRunning}
        onToggle={isRunning ? undefined : () => setExpanded((e) => !e)}
      >
        <div className="space-y-1.5">
          {err && (
            <DetailPanel label="Error" tone="error"><DetailPre tone="error">{err}</DetailPre></DetailPanel>
          )}
          {saved && (
            <div className="text-xs text-zinc-500">
              Saved to <span className="font-mono text-zinc-700">{saved}</span>
            </div>
          )}
          {result?._warning && (
            <div className="text-xs text-yellow-700 break-words">{result._warning}</div>
          )}
          <DetailPanel label="Result">
            <DetailPre maxH="max-h-[260px]">{safeStringify(result)}</DetailPre>
          </DetailPanel>
        </div>
      </ToolRow>

      {succeeded && <GeneratedFiles result={result} />}

      {/* Live progress while running: a thin bar, the figure, the phase. */}
      {isRunning && (
        <div className="mb-1.5 mt-1 max-w-md pl-[22px]">
          <div className="mb-1.5 flex items-baseline justify-between gap-3 text-meta">
            <div className="flex min-w-0 items-baseline gap-2">
              {indeterminate ? (
                <span className="truncate text-zinc-700">
                  {live?.phase === 'warming' ? 'Warming worker' : 'Working'}
                </span>
              ) : (
                <span className="tabular-nums text-zinc-900">{Math.round(percent ?? 0)}%</span>
              )}
              {frames && (
                <span className="shrink-0 font-mono text-xs tabular-nums text-zinc-400">
                  {frames.done}/{frames.total}
                </span>
              )}
            </div>
            {etaValue ? (
              <span className="shrink-0 text-xs tabular-nums text-zinc-500">{etaValue} left</span>
            ) : etaText ? (
              <span className="shrink-0 text-xs text-zinc-500">{etaText}</span>
            ) : null}
          </div>

          <div
            className="h-1 overflow-hidden rounded-full bg-zinc-100"
            role="progressbar"
            aria-label={phaseLabel ?? cfg.title}
            aria-valuemin={0}
            aria-valuemax={100}
            {...(indeterminate ? {} : { 'aria-valuenow': Math.round(percent ?? 0) })}
          >
            {indeterminate ? (
              <div className="h-full w-1/3 rounded-full bg-zinc-400 animate-progress-indeterminate motion-reduce:animate-none motion-reduce:w-full motion-reduce:bg-zinc-300" />
            ) : (
              <div
                className="h-full rounded-full bg-zinc-800 transition-[width] duration-700 ease-out"
                style={{ width: `${Math.min(100, Math.max(2, percent ?? 0))}%` }}
              />
            )}
          </div>

          {/* Which step of the pipeline this is. */}
          <div className="mt-1.5 flex items-center gap-2">
            {phaseAt !== -1 && (
              <div className="flex shrink-0 items-center gap-1" aria-hidden>
                {PHASE_RAILS[cfg.kind].map((p, i) => (
                  <span
                    key={p}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i < phaseAt ? 'w-1 bg-zinc-400'
                        : i === phaseAt ? 'w-3 bg-zinc-800'
                        : 'w-1 bg-zinc-200'
                    }`}
                  />
                ))}
              </div>
            )}
            <span className="truncate text-xs text-zinc-500">
              {live?.phase === 'warming'
                ? 'loading models into the GPU'
                : (phaseLabel ?? 'starting…')}
              {live?.estimated && ' · estimated'}
            </span>
          </div>

          {live?.stalled && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-yellow-700">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              no heartbeat — the runner may be recovering
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function makeJobToolUI(cfg: JobUIConfig) {
  return makeAssistantToolUI<any, any>({
    toolName: cfg.toolName,
    render: (props: any) => <JobCard cfg={cfg} {...props} />,
  });
}

export const VcRenderStatusToolUI = makeJobToolUI({
  toolName: 'vc_render_status',
  kind: 'vc-render',
  title: 'Rendering video',
  Icon: Film,
});

export const VcGetPreviewToolUI = makeJobToolUI({
  toolName: 'vc_get_preview',
  kind: 'vc-preview',
  title: 'Rendering scene preview',
  Icon: Clapperboard,
});

export const VfxGetJobToolUI = makeJobToolUI({
  toolName: 'vfx_get_job',
  kind: 'vfx-job',
  title: 'Running pipeline job',
  Icon: Cpu,
});
